import React, { type FC, type RefObject, useEffect, useRef } from "react";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import styles from "./Interior360.module.scss";
import useInteriorSpin3D from "../../../../../../../shared-logic/hooks/useInteriorSpin3D";
import { trackActivateInterior360Spin } from "../../../../../utils/tracking";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import { useWindowDimensionsContext } from "../../../../../../../../common-deprecated/containers/WindowDimensionsProvider";
import { SteeringPosition } from "../../../../../../../shared-logic/types/CarType";
import { useCarConfigLabel } from "../../../../../utils/labelUtils";
import { addNotification } from "../../../../../../../../common-deprecated/redux/actions/NotificationActions";

type InteriorSpinType = {
    initialized: boolean;
    dragged: boolean;
};

const useInteriorSpin = (
    canvas: RefObject<HTMLCanvasElement>,
    enabled: boolean,
    images: string[],
): InteriorSpinType => {
    const dispatch = useCarConfigDispatch();
    const { width, height } = useWindowDimensionsContext();
    const steering = useCarConfigSelector((state) => state.car.model.steering);
    const [interiorSpinNotificationTitle, intertiorSpinNotificationDescription] = useCarConfigLabel([
        "carConfigA11yInteriorSpinIsLoaded",
        "carConfigA11yInteriorSpinIsLoadedDescription",
    ]);

    // -------------------------------------------
    // Interact with scene
    // -------------------------------------------

    const { initialized, dragged, updateCanvasSize, updateTexture } = useInteriorSpin3D(canvas, enabled, {
        x: steering === SteeringPosition.Right ? -3 : 3,
        y: -1.4,
        z: 3.86, // 3.86 is a magic number to compensate the 70 fov, see #8573
        fov: 70, // fov set to 70 to reduce fisheye, see #8573
    });

    // Update textures once they are preloaded or when scene is setup, whichever is first
    useEffect(() => {
        if (initialized && enabled) {
            updateTexture(images);
        }

        if (enabled && initialized) {
            dispatch(
                addNotification([
                    {
                        title: interiorSpinNotificationTitle,
                        description: intertiorSpinNotificationDescription,
                        srOnly: true,
                    },
                ]),
            );
        }
    }, [enabled, images.join(","), initialized, updateTexture]);

    // Update canvas size on window resize through window context (updateCanvasSize checks for and stops unneeded resizes)
    useEffect(() => {
        if (!initialized || !enabled) return;
        // start render and listen for window resizes
        updateCanvasSize();
    }, [width, height, initialized, updateCanvasSize, enabled]);

    return { initialized, dragged };
};

const Interior360: FC<{
    images: string[];
    enabled: boolean;
    className?: string;
}> = ({ images, className, enabled }) => {
    const section = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const canvasElement = useRef<HTMLCanvasElement>(null);
    const { dragged } = useInteriorSpin(canvasElement, enabled, images);

    useEffect(() => {
        if (dragged) {
            trackActivateInterior360Spin(section);
        }
    }, [dragged]);

    return (
        <div className={className}>
            {!dragged && (
                <div className={styles.icon}>
                    <Icon variant="360-spin" aria-hidden="true" />
                </div>
            )}
            <canvas
                ref={canvasElement}
                style={{
                    width: "100%",
                    height: "100%",
                }}
                className={styles.canvas}
            />
        </div>
    );
};

export default Interior360;
